import { DateTime } from 'luxon';
import { isEmpty, isNil } from 'lodash';
import { isString } from 'class-validator';

import { toDateTime, formatDateTimeWithMilitarySupport, LuxonDateTimeFormats } from './chronon';
import type { DateLike } from './nova-date';
import {
  makeCompanySettings,
  type CompanySettingsInput,
  type DateDisplayFormat,
  type ISettings,
  type TimeDisplayFormat
} from './settings';

/** Format pattern strings for each company {@link DateDisplayFormat} setting value. */
export const COMPANY_DATE_FORMAT_PATTERNS: Record<DateDisplayFormat, string> = {
  MDY: 'MM/dd/yyyy',
  DMY: 'dd/MM/yyyy',
  YMD: 'yyyy-MM-dd',
  weekdayMDY: 'EEE, MM/dd/yyyy',
  weekdayDMY: 'EEE, dd/MM/yyyy',
  weekdayYMD: 'EEE, yyyy-MM-dd'
};

/**
 * Numeric-only sibling for each date display format (order unchanged).
 * Exhaustive: a new {@link DateDisplayFormat} fails typecheck until mapped here.
 */
export const NUMERIC_DATE_DISPLAY_FORMAT: Record<DateDisplayFormat, DateDisplayFormat> = {
  MDY: 'MDY',
  DMY: 'DMY',
  YMD: 'YMD',
  weekdayMDY: 'MDY',
  weekdayDMY: 'DMY',
  weekdayYMD: 'YMD'
};

/** Format pattern strings for each company {@link TimeDisplayFormat} setting value. */
export const COMPANY_TIME_FORMAT_PATTERNS: Record<TimeDisplayFormat, string> = {
  twelveHour: 'h:mm a',
  twentyFourHour: 'HH:mm'
};

export interface CompanyDisplayFormatOptions {
  /** Carrier company (or any object with optional `settings`). Defaults apply when null or partial. */
  company?: CompanySettingsInput;
  /** Warehouse IANA timezone — determines the local calendar date/time for the instant. */
  timezone?: string;
  /**
   * Locale for weekday abbreviations in date formats that include `EEE`.
   * Does not change numeric date order (controlled by company settings).
   */
  locale?: string;
}

/** Options for the company-agnostic Luxon pattern formatter. */
export interface FormatDateOptions {
  /** Luxon format pattern (e.g. `MM/dd/yyyy`, `EEE, dd/MM/yyyy HH:mm`). */
  format: string;
  /** Warehouse IANA timezone — determines the local calendar date/time for the instant. */
  timezone?: string;
  /** Locale for tokens such as `EEE` / `a`. */
  locale?: string;
}

export type ResolvedCompanyDisplayFormats = {
  dateDisplayFormat: DateDisplayFormat;
  timeDisplayFormat: TimeDisplayFormat;
  dateFormatPattern: string;
  timeFormatPattern: string;
  dateTimeFormatPattern: string;
};

/**
 * Resolves effective company display settings and their format pattern strings.
 * Uses {@link makeCompanySettings} for US defaults when settings are null or partial.
 */
export function resolveCompanyDisplayFormats(
  company?: CompanySettingsInput
): ResolvedCompanyDisplayFormats {
  const settings = makeCompanySettings(company);
  return buildFormatsFromSettings(settings);
}

function buildFormatsFromSettings(settings: ISettings): ResolvedCompanyDisplayFormats {
  const dateDisplayFormat = settings.dateDisplayFormat as DateDisplayFormat;
  const timeDisplayFormat = settings.timeDisplayFormat as TimeDisplayFormat;
  const dateFormatPattern = COMPANY_DATE_FORMAT_PATTERNS[dateDisplayFormat];
  const timeFormatPattern = COMPANY_TIME_FORMAT_PATTERNS[timeDisplayFormat];

  return {
    dateDisplayFormat,
    timeDisplayFormat,
    dateFormatPattern,
    timeFormatPattern,
    dateTimeFormatPattern: `${dateFormatPattern} ${timeFormatPattern}`
  };
}

export function getCompanyDateFormatPattern(dateDisplayFormat: DateDisplayFormat): string {
  return COMPANY_DATE_FORMAT_PATTERNS[dateDisplayFormat];
}

export function getCompanyTimeFormatPattern(timeDisplayFormat: TimeDisplayFormat): string {
  return COMPANY_TIME_FORMAT_PATTERNS[timeDisplayFormat];
}

export function getCompanyDateTimeFormatPattern(
  dateDisplayFormat: DateDisplayFormat,
  timeDisplayFormat: TimeDisplayFormat
): string {
  return `${COMPANY_DATE_FORMAT_PATTERNS[dateDisplayFormat]} ${COMPANY_TIME_FORMAT_PATTERNS[timeDisplayFormat]}`;
}

/**
 * Maps `weekday*` date display formats to the matching numeric-only format (order unchanged).
 */
export function toNumericCompanyDateDisplayFormat(
  dateDisplayFormat: DateDisplayFormat
): DateDisplayFormat {
  return NUMERIC_DATE_DISPLAY_FORMAT[dateDisplayFormat];
}

function resolveFormats(options: CompanyDisplayFormatOptions): ResolvedCompanyDisplayFormats {
  return resolveCompanyDisplayFormats(options.company);
}

function toLocalizedDateTime(input: DateLike, timezone?: string): DateTime {
  return toDateTime(
    input,
    isString(timezone) && !isEmpty(timezone) ? { zone: timezone } : undefined
  );
}

/**
 * Some locales (e.g. pt-BR) include a trailing period in abbreviated weekdays ("qua.").
 * Combined with our `EEE, …` patterns that yields "qua.," — strip the locale period before the comma.
 */
function stripLocaleWeekdayTrailingDot(formatted: string, format: string): string {
  if (!format.includes('EEE')) {
    return formatted;
  }
  return formatted.replace(/\.,(?<space>\s)/gu, ',$<space>');
}

/**
 * Inverse of {@link stripLocaleWeekdayTrailingDot} for parse round-trips in dotted-abbreviation locales.
 */
function restoreLocaleWeekdayTrailingDot(value: string, format: string): string {
  if (!format.includes('EEE')) {
    return value;
  }
  return value.replace(/(?<weekday>\p{L}+),(?<space>\s)/gu, '$<weekday>.,$<space>');
}

/** Title-case the leading letter so es/pt/fr weekdays match English capitalization (Dom, Seg, …). */
function capitalizeLeadingLetter(value: string): string {
  return value.replace(/^\p{L}/u, letter => letter.toLocaleUpperCase());
}

function formatWithLocale(dateTime: DateTime, format: string, locale?: string): string {
  const formatted = !isEmpty(locale)
    ? dateTime.toFormat(format, { locale })
    : dateTime.toFormat(format);
  return stripLocaleWeekdayTrailingDot(formatted, format);
}

/** Options for appointment-email datetime formatters. */
export type AppointmentEmailDateTimeFormatOptions = CompanyDisplayFormatOptions & {
  /**
   * Title-case the leading weekday letter (e.g. `jue` → `Jue`).
   * Intended for the email title (`appointmentDate`) only — leave off for status timestamps.
   */
  capitalizeWeekday?: boolean;
};

function buildParseFormatOptions(
  options: Pick<CompanyDisplayFormatOptions, 'timezone' | 'locale'>
): { zone?: string; locale?: string } {
  return {
    ...(isString(options.timezone) && !isEmpty(options.timezone) ? { zone: options.timezone } : {}),
    ...(isString(options.locale) && !isEmpty(options.locale) ? { locale: options.locale } : {})
  };
}

function parseWithCompanyFormat(
  value: string,
  format: string,
  options: CompanyDisplayFormatOptions
): DateTime | null {
  const parseOptions = buildParseFormatOptions(options);
  const trimmed = value.trim();
  let parsed = DateTime.fromFormat(trimmed, format, parseOptions);
  if (!parsed.isValid && format.includes('EEE')) {
    const withLocaleDots = restoreLocaleWeekdayTrailingDot(trimmed, format);
    if (withLocaleDots !== trimmed) {
      parsed = DateTime.fromFormat(withLocaleDots, format, parseOptions);
    }
  }
  return parsed.isValid ? parsed : null;
}

/** Formats an instant with an explicit Luxon pattern (company-agnostic). */
export function formatDate(input: DateLike, options: FormatDateOptions): string {
  const dateTime = toLocalizedDateTime(input, options.timezone);
  return formatWithLocale(dateTime, options.format, options.locale);
}

/** Formats an instant as a date string using company display preferences. */
export function formatCompanyDate(
  input: DateLike,
  options: CompanyDisplayFormatOptions = {}
): string {
  const { dateFormatPattern } = resolveFormats(options);
  return formatDate(input, {
    format: dateFormatPattern,
    timezone: options.timezone,
    locale: options.locale
  });
}

/** Formats an instant as a time string using company display preferences. */
export function formatCompanyTime(
  input: DateLike,
  options: CompanyDisplayFormatOptions = {}
): string {
  const { timeFormatPattern } = resolveFormats(options);
  return formatDate(input, {
    format: timeFormatPattern,
    timezone: options.timezone,
    locale: options.locale
  });
}

/** Formats an instant as a combined date and time string using company display preferences. */
export function formatCompanyDateTime(
  input: DateLike,
  options: CompanyDisplayFormatOptions = {}
): string {
  const { dateTimeFormatPattern } = resolveFormats(options);
  return formatDate(input, {
    format: dateTimeFormatPattern,
    timezone: options.timezone,
    locale: options.locale
  });
}

/**
 * True when the company has explicitly set at least one date/time display preference.
 * Used by appointment emails to decide company patterns vs legacy long prose.
 */
export function hasExplicitCompanyDisplayPreferences(company?: CompanySettingsInput): boolean {
  const settings = company?.settings;
  if (isNil(settings)) {
    return false;
  }

  return !isNil(settings.dateDisplayFormat) || !isNil(settings.timeDisplayFormat);
}

/**
 * Resolves the Luxon date+time pattern for appointment emails / timestamp custom fields.
 * Uses carrier company display preferences only (no warehouse military override).
 */
export function resolveAppointmentEmailDateTimeFormatPattern(
  options: CompanyDisplayFormatOptions = {}
): string {
  return resolveFormats(options).dateTimeFormatPattern;
}

/**
 * Formats an instant for carrier appointment emails using company display preferences.
 * Same pattern as {@link formatCompanyDateTime}, plus an abbreviated named offset `(ZZZZ)`
 * in English (falls back to the IANA zone when Luxon emits a numeric GMT offset).
 */
export function formatAppointmentEmailDateTime(
  input: DateLike,
  options: AppointmentEmailDateTimeFormatOptions = {}
): string {
  const dateTime = toLocalizedDateTime(input, options.timezone);
  const zoneLabel = resolveAppointmentEmailZoneLabel(dateTime, options.timezone);
  const formatted = `${formatCompanyDateTime(input, options)} (${zoneLabel})`;
  return options.capitalizeWeekday === true ? capitalizeLeadingLetter(formatted) : formatted;
}

/**
 * Legacy email long prose (`EEEE, DD @ t`) with optional locale for weekday/month names.
 * Used when the new email engine is on but the carrier company has no display preferences.
 * Zone label is appended separately so locale does not corrupt the offset.
 */
export function formatLegacyAppointmentEmailDateTime(
  input: DateLike,
  options: AppointmentEmailDateTimeFormatOptions & { formatAsMilitary?: boolean } = {}
): string {
  const timezone = options.timezone;
  const locale = !isEmpty(options.locale) ? options.locale : 'en';
  const dateTime = toLocalizedDateTime(input, timezone);
  // Keep the localized ISO (not UTC) so omitting `timezone` still preserves the input zone
  // via formatDateTimeWithMilitarySupport's setZone:true path.
  const formatted = formatDateTimeWithMilitarySupport(
    dateTime.toISO() ?? String(input),
    timezone ?? null,
    LuxonDateTimeFormats.LongDateTimeShortMonth,
    options.formatAsMilitary === true,
    LuxonDateTimeFormats.LongDateTime24ShortMonth,
    locale
  );
  const zoneLabel = resolveAppointmentEmailZoneLabel(dateTime, timezone);
  const withZone = `${formatted} (${zoneLabel})`;
  return options.capitalizeWeekday === true ? capitalizeLeadingLetter(withZone) : withZone;
}

/**
 * Prefer a stable English abbreviated offset for email; remap GMT±N to the IANA zone name.
 * Zone label is resolved separately so YMD dates (`yyyy-MM-dd`) are never rewritten.
 */
function resolveAppointmentEmailZoneLabel(dateTime: DateTime, timezone?: string): string {
  const abbreviatedOffset = dateTime.setLocale('en').toFormat('ZZZZ');
  if (
    /^(?<gmtPrefix>GMT)?[+-]\d/u.test(abbreviatedOffset) &&
    isString(timezone) &&
    !isEmpty(timezone)
  ) {
    return timezone;
  }
  return abbreviatedOffset;
}

/** Parses a date string that was formatted with {@link formatCompanyDate} (same pattern). */
export function parseCompanyDate(
  value: string,
  options: CompanyDisplayFormatOptions = {}
): DateTime | null {
  const { dateFormatPattern } = resolveFormats(options);
  return parseWithCompanyFormat(value, dateFormatPattern, options);
}

/** Parses a time string that was formatted with {@link formatCompanyTime} (same pattern). */
export function parseCompanyTime(
  value: string,
  options: CompanyDisplayFormatOptions = {}
): DateTime | null {
  const { timeFormatPattern } = resolveFormats(options);
  return parseWithCompanyFormat(value, timeFormatPattern, options);
}

/** Parses a combined date/time string formatted with {@link formatCompanyDateTime}. */
export function parseCompanyDateTime(
  value: string,
  options: CompanyDisplayFormatOptions = {}
): DateTime | null {
  const { dateTimeFormatPattern } = resolveFormats(options);
  return parseWithCompanyFormat(value, dateTimeFormatPattern, options);
}
